import { useMemo } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import css from './LangSelector.module.less';

export interface LangOption { value: string; label: string; }

// normalizeLang 已抽到独立 util,这里 re-export 保持现有调用方
// `import { normalizeLang } from './LangSelector'` 不需要改。
export { normalizeLang } from '../utils/normalizeLang';
import { normalizeLang } from '../utils/normalizeLang';

interface LangSelectorProps {
  allOptions: LangOption[];
  desktopLangs: string[];                  // 桌面端在翻的语言
  audienceLangs: string[];                 // 观众已订阅的语言
  selectedLang: string;                    // 当前选定查看的语言
  onSelectLang: (lang: string) => void;    // 切换查看语言
  // 订阅扩展语言;releaseLang = 满员换语言时要释放的旧语言。返回是否成功
  onSubscribe: (lang: string, releaseLang?: string) => Promise<boolean>;
}

export function LangSelector({
  allOptions,
  desktopLangs,
  audienceLangs,
  selectedLang,
  onSelectLang,
  onSubscribe,
}: LangSelectorProps) {
  const { t } = useTranslation();

  // PRD 2026-06-17: 观众端总语言数 cap = 4 (desktopLangs ∪ audience_extras)
  const TOTAL_CAP = 4;

  // 所有集合用 normalized key (避免 zh_hans 跟 zh 不匹配的问题)
  const desktopSet = useMemo(
    () => new Set(desktopLangs.map(normalizeLang)),
    [desktopLangs],
  );
  const audienceSet = useMemo(
    () => new Set(audienceLangs.map(normalizeLang)),
    [audienceLangs],
  );

  // 当前总数 = desktop 已翻的 + 观众扩展的 (按 normalized lang 去重)
  const currentTotal = useMemo(() => {
    const union = new Set<string>();
    desktopSet.forEach((l) => union.add(l));
    audienceSet.forEach((l) => union.add(l));
    return union.size;
  }, [desktopSet, audienceSet]);

  const limitReached = currentTotal >= TOTAL_CAP;

  const desktopGroup = allOptions.filter((o) => desktopSet.has(normalizeLang(o.value)));
  const extendGroup = allOptions.filter((o) => !desktopSet.has(normalizeLang(o.value)));

  // 满员时能否"换语言": 自己当前查看的是 extra 语言(桌面语言不占 audience 名额,换走腾不出位置)。
  // 前端不知道该语言是否还有其他观众在用 —— 交给后端原子判定:换不出名额会拒绝并恢复原订阅。
  const canSwap = !!selectedLang && !desktopSet.has(normalizeLang(selectedLang));

  const handleClick = async (opt: LangOption, isDesktop: boolean, isSubscribed: boolean) => {
    if (isDesktop) {
      onSelectLang(opt.value);
      return;
    }
    if (isSubscribed) {
      // 已订阅 → 切到查看
      onSelectLang(opt.value);
      return;
    }
    // 未订阅:点击 = 订阅(若未达上限)
    if (limitReached) {
      if (!canSwap) {
        message.warning(
          t('share.lang.limitReachedToast', {
            defaultValue: 'Limit reached, cannot subscribe to more languages',
          }),
        );
        return;
      }
      // 满员换语言: 后端原子完成"释放旧语言→检查名额→订阅新语言",失败保持原语言(toast 在上层)
      const ok = await onSubscribe(opt.value, selectedLang);
      if (ok) onSelectLang(opt.value);
      return;
    }
    onSubscribe(opt.value);
    onSelectLang(opt.value);
  };

  return (
    <div className={css.selector}>
      {/* 顶部固定: 桌面已翻 + extend group header (limit 时 contactLink 才有意义可见) */}
      <div className={css.fixedTop}>
        {desktopGroup.length > 0 && (
          <>
            <div className={css.groupHeader}>
              {t('share.lang.desktopGroup', { defaultValue: 'Already translated by host' })}
            </div>
            {desktopGroup.map((opt) => (
              <div
                key={opt.value}
                className={clsx(css.item, css.desktop, selectedLang === opt.value && css.selected)}
                onClick={() => handleClick(opt, true, true)}
              >
                <span className={css.name}>{opt.label}</span>
                <span className={css.tag}>{t('share.lang.tagDesktop', { defaultValue: "Host's" })}</span>
              </div>
            ))}
          </>
        )}

        <div className={css.groupHeader}>
          {t('share.lang.extendGroup', { defaultValue: 'Additional languages' })}
          <span className={css.limit}>
            {t('share.lang.totalLimit', {
              count: currentTotal,
              defaultValue: `(${currentTotal}/${TOTAL_CAP} total)`,
            })}
          </span>
        </div>
      </div>

      {/* 中间可滚动: extend lang chip 列表 (语种多时不挤掉底部 contactLink) */}
      <div className={css.scrollList}>
        {extendGroup.map((opt) => {
          const subscribed = audienceSet.has(normalizeLang(opt.value));
          const isSelected = selectedLang === opt.value;
          // 满员但可换语言(canSwap)时不锁死: 点击走原子换语言
          const locked = !subscribed && limitReached && !canSwap;
          return (
            <div
              key={opt.value}
              className={clsx(
                css.item,
                isSelected && css.selected,
                locked && css.locked,
              )}
              onClick={() => handleClick(opt, false, subscribed)}
            >
              <span className={css.name}>{opt.label}</span>
              {/* 高亮只给"自己正在看"的语言;其他观众订阅的语言用右侧小字标记区分,
                  避免整块高亮导致分不清自己选的是哪个。
                  不暴露退订按钮:切走查看语言时前端自动退订自己那份(观众粒度,
                  见 share/index.tsx),语言无人订阅后由后端立即/最迟 5 分钟释放。 */}
              {subscribed && !isSelected && (
                <span className={css.inUseTag}>
                  {t('share.lang.tagInUse', { defaultValue: 'In use' })}
                </span>
              )}
              {locked && (
                <span className={css.lockHint}>
                  {t('share.lang.limitReached', { defaultValue: 'Limit reached' })}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* 底部固定: 上限到达时联系开通入口, 不被 extend 列表挤掉 */}
      {limitReached && (
        <a
          className={css.contactLink}
          href={import.meta.env.VITE_FELO_CONTACT_URL || 'https://subtitles.felo.me/#contact'}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('share.lang.contactToUnlock', { defaultValue: 'Need more? Contact us to unlock' })}
        </a>
      )}
    </div>
  );
}
